import PhotoSwipe from 'photoswipe';
import PhotoSwipeUI_Default from 'photoswipe/dist/photoswipe-ui-default';
import { PhotoSwipeSharedOptions } from './shared-options';
import { VisibilityObserver } from './observers';

const ZOOMED_IN_CLASS = 'pswp--zoomed-in';
const ZOOM_IN_FINISHED_CUSTOM_CLASS = 'pswp--zoom-in-finished-custom';

/**
 * @param {HTMLElement} pswpElem
 * @param {PhotoSwipe.Item[]} items
 * @param {HTMLElement[]} triggerElems
 * @param {PhotoSwipeUI_Default.Options} [options]
 */
export function setupPhotoSwipe(pswpElem, items, [...triggerElems], options) {
    for (const elem of triggerElems) {
        elem.addEventListener('click', event => {
            const { currentSrc } = elem.querySelector('picture img') || elem.querySelector('img') || elem;
            const pswpIndex = parseInt(elem.dataset.pswpindex);

            // Set msrc to image source that is already loaded
            if (!Number.isNaN(pswpIndex) && currentSrc) {
                items[pswpIndex].msrc = currentSrc;
            }

            initPhotoSwipe(pswpElem, items, elem, triggerElems, {
                ...PhotoSwipeSharedOptions,
                ...options,
            });

            // Note that preventDefault does not fire if PhotoSwipe does not initialize correctly
            event.preventDefault();
        });
    }
}

function initPhotoSwipe(pswpElem, items, triggerElem, triggerElems, options) {
    const visibilityObserver = new VisibilityObserver(triggerElems, {
        root: document.body,
        threshold: 0.01,
    });

    const gallery = new PhotoSwipe(pswpElem, PhotoSwipeUI_Default, items, {
        ...options,
        index: parseInt(triggerElem.dataset.pswpindex),
        getThumbBoundsFn: pswpIndex => getAnimationBounds(pswpIndex, triggerElem, triggerElems, visibilityObserver),
    });

    // Make sure VisibilityObserver runs only when PhotoSwipe is opened
    gallery.listen('initialZoomIn', () => visibilityObserver.start());
    gallery.listen('initialZoomOut', () => visibilityObserver.end());

    // ResizeObserver is used to add custom classes related to zooming for styling purposes
    const resizeObserver = new ResizeObserver(([entry]) => onImageSizeChange(entry, pswpElem));

    // This is needed because when PhotoSwipe is first opened then afterChange triggers before img is painted
    let firstImagePainted = false;
    gallery.listen('parseVerticalMargin', () => {
        const img = gallery.currItem.container?.querySelector('.pswp__img:not(.pswp__img--placeholder)');

        if (!firstImagePainted && img) {
            firstImagePainted = true;
            resizeObserver.disconnect();
            resizeObserver.observe(img);
        }
    });

    // Make sure ResizeObserver runs only on the current image
    gallery.listen('afterChange', () => resizeObserver.observe(gallery.currItem.container.lastChild));
    gallery.listen('beforeChange', () => resizeObserver.disconnect());
    gallery.listen('destroy', () => resizeObserver.disconnect());

    gallery.init();
}

function getAnimationBounds(pswpIndex, triggerElem, triggerElems, visibilityObserver) {
    let animationElem;

    if (!visibilityObserver.started) {
        animationElem = triggerElem;
    } else {
        // Returns elements with same image as currently shown in photoswipe and are visible
        const possibleElems = triggerElems.filter(elem => {
            const { pswpindex, observeIndex } = elem.dataset;
            return parseInt(pswpindex) === pswpIndex && visibilityObserver.elemsVisibilityMap[observeIndex];
        });

        if (possibleElems.includes(triggerElem)) {
            animationElem = triggerElem;
        } else {
            animationElem = possibleElems.length ? possibleElems[0] : triggerElem;
        }
    }

    const rect = animationElem.getBoundingClientRect();
    const pageXScroll = window.pageXOffset || document.documentElement.scrollLeft;
    const pageYScroll = window.pageYOffset || document.documentElement.scrollTop;

    return {
        x: rect.left + pageXScroll,
        y: rect.top + pageYScroll,
        w: rect.width,
    };
}

/**
 * Note that due to PhotoSwipe implementation the ResizeObserver triggers this function
 * only when zoom in/out transition ends (or when viewport size changes)
 */
function onImageSizeChange(entry, pswpElem) {
    if (pswpElem.classList.contains(ZOOMED_IN_CLASS)) {
        pswpElem.classList.add(ZOOM_IN_FINISHED_CUSTOM_CLASS);
    } else {
        pswpElem.classList.remove(ZOOM_IN_FINISHED_CUSTOM_CLASS);
    }

    // This is used to place arrows next to image
    const width = entry.borderBoxSize[0].inlineSize;
    document.documentElement.style.setProperty('--photoswipe-image-width', `${width}px`);
}

// For php blade inline scripts
window.setupPhotoSwipe = setupPhotoSwipe;
